import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectLanguage } from '../redux/cartSlice';
import { translations } from '../utils/translations';
import AboutHero from './AboutHero';
import Swal from 'sweetalert2';
import './Login.css';

const ct = {
  password: {
    en: "Password",
    nl: "Wachtwoord",
    es: "Contraseña",
    fr: "Mot de passe",
    de: "Passwort",
    it: "Password",
    pt: "Senha",
    el: "Κωδικός πρόσβασης"
  },
  signUp: {
    en: "Sign Up",
    nl: "Aanmelden",
    es: "Registrarse",
    fr: "S'inscrire",
    de: "Registrieren",
    it: "Registrati",
    pt: "Cadastrar",
    el: "Εγγραφή"
  }
};

const Login = () => {
  const selectedLang = useSelector(selectLanguage) || { code: 'en' };
  const t = translations[selectedLang.code || 'en'] || translations.en;
  
  const getLocText = (key) => {
    return ct[key]?.[selectedLang.code] || ct[key]?.en || "";
  };

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    document.title = `${t.signIn || 'Sign In'} | Smart Telecom Store`;
    window.scrollTo(0, 0);
  }, [t]);

  const handleNavigate = (path, e) => {
    e.preventDefault();
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Show processing state
    Swal.fire({
      title: t.signingIn || 'Signing In...',
      text: t.validatingCredentials || 'Validating credentials with secure server.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          Swal.fire({
            title: t.authFailed || 'Authentication Failed',
            text: data.error,
            icon: 'error',
            confirmButtonColor: '#be1e2d'
          });
        } else {
          const welcomeMsg = (t.welcomeBack || "Welcome back, {name}! Redirecting to homepage...").replace('{name}', data.user.fullName);
          Swal.fire({
            title: t.success || 'Success!',
            text: welcomeMsg,
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          }).then(() => {
            localStorage.setItem('user_session', JSON.stringify(data.user));
            window.dispatchEvent(new Event('auth_change'));
            window.history.pushState({}, '', '/');
            window.dispatchEvent(new PopStateEvent('popstate'));
          });
        }
      })
      .catch((err) => {
        console.error('Login error:', err);
        Swal.fire({
          title: t.connectionErrorTitle || 'Connection Error',
          text: t.authConnectionErrorText || t.connectionErrorText || 'Could not connect to authentication server.',
          icon: 'error',
          confirmButtonColor: '#be1e2d'
        });
      });
  };

  return (
    <>
      <AboutHero />
      <div className="auth-page-wrapper">
        <div className="auth-container">
          {/* Left Column: Image */}
          <div className="auth-image-col">
            <img 
              src="https://images.unsplash.com/photo-1527689368864-3a821dbccc34?w=800&auto=format&fit=crop&q=80" 
              alt="Sign In to Smart Telecom" 
              className="auth-image" 
            />
          </div>

          {/* Right Column: Form */}
          <div className="auth-form-col">
            <div className="auth-form-box">
              <h1 className="auth-heading">{t.signIn || 'Sign In'}</h1>
              <p className="auth-subheading">{t.authSubheadingLogin || 'Enter your details below to access your account'}</p>

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="auth-input-group">
                  <label htmlFor="email">{t.emailAddress || 'Email Address'}</label>
                  <input 
                    type="email" 
                    id="email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.emailPlaceholder || 'Enter your email'} 
                    required 
                  />
                </div>

                <div className="auth-input-group">
                  <div className="label-row">
                    <label htmlFor="password">{getLocText('password')}</label>
                    <a 
                      href="#forgot" 
                      className="forgot-password-link"
                      onClick={(e) => e.preventDefault()}
                    >
                      {t.forgotPassword || 'Forgot Password?'}
                    </a>
                  </div>
                  <input 
                    type="password" 
                    id="password" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t.passwordPlaceholder || 'Enter your password'} 
                    required 
                  />
                </div>

                <div className="auth-options-row">
                  <label className="remember-me-checkbox">
                    <input 
                      type="checkbox" 
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <span className="checkbox-custom-box"></span>
                    {t.rememberMe || 'Remember me'}
                  </label>
                </div>

                <button type="submit" className="auth-submit-btn-primary">
                  {t.signIn || 'Sign In'}
                </button>
              </form>

              <p className="auth-switch-text">
                {t.dontHaveAccount || "Don't have an account?"}{' '}
                <a 
                  href="/register" 
                  onClick={(e) => handleNavigate('/register', e)}
                  className="auth-switch-link"
                >
                  {getLocText('signUp')}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;

